<script setup lang="ts">
/**
 * Tag — flat label for plan names, categories, taxonomies.
 *
 * Different from Chip: tags are descriptive, not interactive, and
 * carry no border. The prototype uses tags for things like plan
 * names ("Sports Premium", "Family", "Drama Unlimited").
 *
 * Variants follow the functional color palette. Default is a
 * neutral surface tone for unclassified labels.
 */
import { computed } from 'vue'

interface Props {
  variant?: 'default' | 'brand' | 'green' | 'amber' | 'red' | 'blue' | 'violet'
}

const props = withDefaults(defineProps<Props>(), { variant: 'default' })

const classes = computed(() => {
  const base = 'inline-flex items-center h-5 px-1.5 text-[10px] font-medium rounded-sm uppercase tracking-wide'
  return `${base} ${
    {
      default: 'bg-surface-2 text-text-3',
      brand: 'bg-brand-soft text-brand-deep',
      green: 'bg-green-soft text-green',
      amber: 'bg-amber-soft text-amber',
      red: 'bg-red-soft text-red',
      blue: 'bg-blue-soft text-blue',
      violet: 'bg-violet-soft text-violet',
    }[props.variant]
  }`
})
</script>

<template>
  <span :class="classes"><slot /></span>
</template>
